/**
 * Short term storage for the AG Grid filter model of each grid.
 *
 * Filters are working state, not a preference: you want the ones you just set to
 * survive opening a job and coming back, or a reload, but you do not want last
 * week's filter still applied when you return. So this is sessionStorage - it dies
 * with the browser tab - rather than the server, where the column layouts live.
 * Keys are namespaced per user, and sign out clears them, so nothing a user
 * filtered on is left behind on a shared machine.
 */

const FILTER_PREFIX = 'gridFilters:';

// Matches utils/gridLayoutStorage: PrivateRoute only renders a grid once the user
// is loaded, so this is really only reached in mock mode.
const NO_USER = 'anonymous';

const userPrefix = (userId)=> `${FILTER_PREFIX}${userId || NO_USER}:`;

const filterKey = (userId, gridKey)=> `${userPrefix(userId)}${gridKey}`;

const isFilterKey = (key)=> typeof key === 'string' && key.startsWith(FILTER_PREFIX);

const isEmptyModel = (filterModel)=> !filterModel || Object.keys(filterModel).length === 0;

const readFilterModel = (userId, gridKey)=> {
  if(!gridKey) return null;

  try {
    const raw = sessionStorage.getItem(filterKey(userId, gridKey));
    if(!raw) return null;

    const filterModel = JSON.parse(raw);
    return isEmptyModel(filterModel) ? null : filterModel;
  } catch(e) {
    return null;
  }
};

/**
 * Stores the grid's filter model, or drops the entry when the grid has no filters
 * left. Clearing the filters on a grid is therefore all it takes to forget them -
 * the clear-all control relies on that.
 */
const saveFilterModel = (userId, gridKey, filterModel)=> {
  if(!gridKey) return;

  try {
    if(isEmptyModel(filterModel)) {
      sessionStorage.removeItem(filterKey(userId, gridKey));
      return;
    }

    sessionStorage.setItem(filterKey(userId, gridKey), JSON.stringify(filterModel));
  } catch(e) {}
};

const removeFilterModel = (userId, gridKey)=> {
  if(!gridKey) return;

  try {
    sessionStorage.removeItem(filterKey(userId, gridKey));
  } catch(e) {}
};

/**
 * Forgets every grid's filters, for every user in this tab. Called on sign out and
 * on session expiry, next to the `localStorage.clear()` that drops the session.
 */
const clearAllFilterModels = ()=> {
  try {
    const keys = [];
    for(let i = 0; i < sessionStorage.length; i++) {
      keys.push(sessionStorage.key(i));
    }

    keys.forEach((key)=> {
      if(isFilterKey(key)) sessionStorage.removeItem(key);
    });
  } catch(e) {}
};

export {
  readFilterModel,
  saveFilterModel,
  removeFilterModel,
  clearAllFilterModels,
  isFilterKey,
  filterKey,
  FILTER_PREFIX,
};
